import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import LegalHeader from '../components/LegalHeader';

const PrivacyPolicy = () => {
  const { t } = useLanguage();

  const lastUpdatedDate = '2 de junio de 2026';
  const s = t('privacyPolicy.sections');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <LegalHeader />

      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <div className="flex justify-center items-center space-x-4 mb-4">
            <img
              src="/farm-logo.png"
              alt="FARM Logo"
              className="w-12 h-12 rounded-xl"
            />
            <h1 className="text-3xl font-bold text-gray-900">{t('privacyPolicy.title')}</h1>
          </div>
          <p className="text-gray-600">{t('privacyPolicy.subtitle')}</p>
        </div>
      </div>

      {/* Contenido */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">

          {/* Introducción */}
          <section>
            <p className="text-gray-700 leading-relaxed">
              {t('privacyPolicy.intro.p1')}
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              {t('privacyPolicy.intro.p2')}
            </p>
          </section>

          {/* Responsable del tratamiento */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.dataController.title}</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{s.dataController.companyName}</h3>
                  <p className="text-gray-700">ADVISER GROUP COMMUNITY SLU</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{s.dataController.taxId}</h3>
                  <p className="text-gray-700">B66883687</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{s.dataController.address}</h3>
                  <p className="text-gray-700">
                    Gran Vía Carles III, 98, 10º<br />
                    08028 Barcelona, España
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{s.dataController.contactEmail}</h3>
                  <p className="text-gray-700">
                    <a href="mailto:support@farmway.dev" className="text-blue-600 hover:text-blue-800">
                      support@farmway.dev
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Información que recopilamos */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.dataCollection.title}</h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{s.dataCollection.website.title}</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  {s.dataCollection.website.items.map((item, i) => (
                    <li key={i}><strong>{item.label}:</strong> {item.text}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{s.dataCollection.app.title}</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  {s.dataCollection.app.items.map((item, i) => (
                    <li key={i}><strong>{item.label}:</strong> {item.text}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{s.dataCollection.gemini.title}</h3>
                <p className="text-gray-700 mb-2">{s.dataCollection.gemini.intro}</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  {s.dataCollection.gemini.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Base legal y finalidad */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.legalBasis.title}</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 p-4 text-left font-semibold">{s.legalBasis.tableHeaders.data}</th>
                    <th className="border border-gray-300 p-4 text-left font-semibold">{s.legalBasis.tableHeaders.purpose}</th>
                    <th className="border border-gray-300 p-4 text-left font-semibold">{s.legalBasis.tableHeaders.legalBasis}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.legalBasis.rows.map((row, i) => (
                    <tr key={i}>
                      <td className="border border-gray-300 p-4">{row.data}</td>
                      <td className="border border-gray-300 p-4">{row.purpose}</td>
                      <td className="border border-gray-300 p-4">{row.basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Destinatarios */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.recipients.title}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{s.recipients.intro}</p>
              <ul className="list-disc pl-6 space-y-2">
                {s.recipients.items.map((item, i) => (
                  <li key={i}><strong>{item.label}:</strong> {item.text}</li>
                ))}
              </ul>
              <p className="mt-4">{s.recipients.noSale}</p>
            </div>
          </section>

          {/* Conservación */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.retention.title}</h2>
            <div className="space-y-4 text-gray-700">
              <ul className="list-disc pl-6 space-y-2">
                {s.retention.items.map((item, i) => (
                  <li key={i}><strong>{item.label}:</strong> {item.text}</li>
                ))}
              </ul>
            </div>
          </section>

          {/* Derechos del usuario */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.rights.title}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{s.rights.intro}</p>
              <ul className="list-disc pl-6 space-y-2">
                {s.rights.items.map((item, i) => (
                  <li key={i}><strong>{item.label}:</strong> {item.text}</li>
                ))}
              </ul>
              <p className="mt-4">
                {s.rights.contact}
                <a href="mailto:support@farmway.dev" className="text-blue-600 hover:text-blue-800 ml-1">
                  support@farmway.dev
                </a>
              </p>
            </div>
          </section>

          {/* Seguridad */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.security.title}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{s.security.intro}</p>
              <ul className="list-disc pl-6 space-y-2">
                {s.security.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </section>

          {/* Menores */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.minors.title}</h2>
            <p className="text-gray-700 leading-relaxed">{s.minors.content}</p>
          </section>

          {/* Transferencias internacionales */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.transfers.title}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{s.transfers.intro}</p>
              <ul className="list-disc pl-6 space-y-2">
                {s.transfers.items.map((item, i) => (
                  <li key={i}><strong>{item.label}:</strong> {item.text}</li>
                ))}
              </ul>
              <p>{s.transfers.guarantee}</p>
            </div>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.cookies.title}</h2>
            <p className="text-gray-700 leading-relaxed">
              {s.cookies.content}
              <a href="/cookie-policy" className="text-blue-600 hover:text-blue-800 ml-1">
                {s.cookies.link}
              </a>.
            </p>
          </section>

          {/* Modificaciones */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.changes.title}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{s.changes.p1}</p>
              <p>{s.changes.p2}</p>
              <p>{s.changes.p3}</p>
            </div>
          </section>

          {/* Autoridad de control */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.authority.title}</h2>
            <p className="text-gray-700 leading-relaxed">
              {s.authority.content} (
              <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                www.aepd.es
              </a>).
            </p>
          </section>

          {/* Información adicional */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.additionalInfo.title}</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{s.additionalInfo.legalActions.title}</h3>
                <p className="text-gray-700 leading-relaxed">{s.additionalInfo.legalActions.content}</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{s.additionalInfo.logs.title}</h3>
                <p className="text-gray-700 leading-relaxed">{s.additionalInfo.logs.content}</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{s.additionalInfo.moreInfo.title}</h3>
                <p className="text-gray-700 leading-relaxed">{s.additionalInfo.moreInfo.content}</p>
              </div>
            </div>
          </section>

          {/* Definiciones */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.definitions.title}</h2>
            <div className="space-y-4">
              {['personalData', 'usageData', 'user', 'dataSubject', 'dataController', 'application'].map((key) => (
                <div key={key}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{s.definitions[key].title}</h3>
                  <p className="text-gray-700">{s.definitions[key].content}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contacto */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{s.contact.title}</h2>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-2">{s.contact.intro}</p>
              <div className="space-y-2">
                <p className="text-gray-700">
                  <strong>{s.contact.email}:</strong>
                  <a href="mailto:support@farmway.dev" className="text-blue-600 hover:text-blue-800 ml-1">
                    support@farmway.dev
                  </a>
                </p>
                <p className="text-gray-700">
                  <strong>{s.contact.addressLabel}:</strong> Gran Vía Carles III, 98, 10º, 08028 Barcelona, España
                </p>
              </div>
            </div>
          </section>

          {/* Fecha de actualización */}
          <section className="border-t pt-6">
            <p className="text-sm text-gray-500">
              {t('privacyPolicy.effectiveDate').replace('{date}', lastUpdatedDate)}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {t('privacyPolicy.lastUpdated').replace('{date}', lastUpdatedDate)}
            </p>
          </section>

        </div>

        {/* Botón de volver */}
        <div className="mt-8 text-center">
          <button
            onClick={() => window.history.back()}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
          >
            {t('privacyPolicy.backButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
